import React, { useState, useEffect, useMemo } from 'react';
import { DatePicker, Select, Typography, Space } from 'antd';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { Option } = Select;

const DateRangeHandler = ({ 
    dateRange,
    onDateRangeChange,
    pointAudits,
    selectedDateRange,
    onDateRangeOptionChange,
}) => {
    const [dateRangeOptions, setDateRangeOptions] = useState([]);



    // Memoized date formatting functions
    const formatters = useMemo(() => ({
        formatDisplay: (date1, date2) => {
            if (date1.isSame(date2, 'day')) {
                return `${date1.format('DD/MM/YYYY HH:mm')} - ${date2.format('HH:mm')}`;
            }
            return `${date1.format('DD/MM/YYYY')} - ${date2.format('DD/MM/YYYY')}`;
        },
        formatValue: (date1, date2) => {
            if (date1.isSame(date2, 'day')) {
                return `${date1.format('YYYY-MM-DD HH:mm')},${date2.format('YYYY-MM-DD HH:mm')}`;
            }
            return `${date1.format('YYYY-MM-DD')},${date2.format('YYYY-MM-DD')}`;
        }
    }), []);

    // Generate date range options based on point audits
    useEffect(() => {
        if (!dateRange?.[0] || !dateRange?.[1]) return;

        const start = dayjs(dateRange[0]);
        const end = dayjs(dateRange[1]);
        const options = [];

        if (pointAudits?.length > 0) {
            // Filter and sort relevant coordinate changes
            const coordinateChanges = pointAudits
                .filter(audit => 
                    audit.changes?.coordinates &&
                    dayjs(audit.changed_at).isBetween(start, end, null, '[]')
                )
                .sort((a, b) => dayjs(a.changed_at).diff(dayjs(b.changed_at)));

            if (coordinateChanges.length > 0) {
                // Add initial range
                options.push({
                    label: formatters.formatDisplay(start, dayjs(coordinateChanges[0].changed_at)),
                    value: formatters.formatValue(start, dayjs(coordinateChanges[0].changed_at))
                });

                // Add intermediate ranges
                for (let i = 0; i < coordinateChanges.length - 1; i++) {
                    const currentDate = dayjs(coordinateChanges[i].changed_at);
                    const nextDate = dayjs(coordinateChanges[i + 1].changed_at);
                    options.push({
                        label: formatters.formatDisplay(currentDate, nextDate),
                        value: formatters.formatValue(currentDate, nextDate)
                    });
                }

                // Add final range
                options.push({
                    label: formatters.formatDisplay(
                        dayjs(coordinateChanges[coordinateChanges.length - 1].changed_at),
                        end
                    ),
                    value: formatters.formatValue(
                        dayjs(coordinateChanges[coordinateChanges.length - 1].changed_at),
                        end
                    )
                });
            }
        }

        // If no audit points or empty options, add full date range
        if (options.length === 0) {
            options.push({
                label: formatters.formatDisplay(start, end),
                value: formatters.formatValue(start, end)
            });
        }

        // Remove any duplicate ranges
        const uniqueOptions = options.filter((option, index, self) =>
            index === self.findIndex((t) => (
                t.value === option.value
            ))
        );

        setDateRangeOptions(uniqueOptions);

        // Set initial selected range if none is selected
        if (!selectedDateRange && uniqueOptions.length > 0) {
            onDateRangeOptionChange(uniqueOptions[0].value);
        }
    }, [dateRange, pointAudits, formatters, selectedDateRange, onDateRangeOptionChange]);

    const handleDisabledDate = (current) => {
        // Disable dates more than 6 months in the past or any future dates
        return current && (current > dayjs());
    };


    return (
        <Space direction="vertical" size="middle" className="w-full">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-full sm:w-auto">
                    <Text strong className="block mb-2">Sélectionnez la période:</Text>
                    <RangePicker
                        className="w-full"
                        value={dateRange}   
                        onChange={onDateRangeChange}
                        disabledDate={handleDisabledDate}
                        presets={[
                            { label: 'Aujourd\'hui', text: 'Aujourd\'hui', value: [dayjs().startOf('day'), dayjs()] },
                            { label: 'La semaine dernière', text: 'La semaine dernière', value: [dayjs().subtract(1, 'week'), dayjs()] },
                            { label: 'Ce mois-ci', text: 'Ce mois-ci', value: [dayjs().startOf('month'), dayjs()] },
                            { label: 'Le mois dernier', text: 'Le mois dernier', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] }
                        ]}
                    />
                </div>

                {dateRangeOptions.length > 0 && (
                    <div className="w-full sm:w-auto">
                        <Text strong className="block mb-2">Select Data Period:</Text>
                        <Select
                            className="w-full sm:w-96"
                            value={selectedDateRange}
                            onChange={onDateRangeOptionChange}
                            placeholder="Select time period"
                        >
                            {dateRangeOptions.map((option, index) => (
                                <Option key={index} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </div>
                )}
            </div>
        </Space>
    );
};

export default DateRangeHandler;